import type { ChangeEvent, SubmitEvent } from 'react'
import {
  VpAccordion,
  VpDivider,
  VpFormControl,
  VpFormLabel,
  VpLink,
  VpToggle,
} from '@vtmn-play/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useProductsContext } from '@/contexts/products/useProductsContext'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'
import { FiltersBarCodeSearch } from './FiltersBarCodeSearch'
import { FiltersBarGroup } from './FiltersBarGroup'

export function FiltersBar() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    filterGroups,
    isFetchingFacets = false,
    loadingFacetGroups = new Set<string>(),
    viewMode,
    customViewTarget,
    sortCriteria,
    sortOrder,
  } = useProductsContext()

  const codeSearchLabel = customViewTarget === 'gallery' && viewMode === 'model'
    ? t('products-page.filters.search-code')
    : t('products-page.filters.search-sm-code')

  const isRefreshingFacets = isFetchingFacets || loadingFacetGroups.size > 0

  const codeFromUrl = searchParams.get('product_code') ?? ''
  const [codeSearch, setCodeSearch] = useState({ urlCode: codeFromUrl, draft: codeFromUrl })
  const [openPanel, setOpenPanel] = useState<string[]>([])

  if (codeFromUrl !== codeSearch.urlCode) {
    setCodeSearch({ urlCode: codeFromUrl, draft: codeFromUrl })
  }

  const productCode = codeSearch.draft

  const getSelectedFiltersCount = () =>
    filterGroups.reduce((count, filterGroup) =>
      count + filterGroup.filters.filter(filter => searchParams.getAll(filterGroup.id).includes(filter.id)).length, 0)

  const onToggleNewProductsOnly = (event: ChangeEvent<HTMLInputElement>) => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCT_NEW_PRODUCT_ONLY_TOGGLED, {
      action: event.target.checked ? 'enable' : 'disable',
      selected_filters_count: getSelectedFiltersCount(),
      new_product_only: event.target.checked,
      view_mode: viewMode,
      sort_criteria: sortCriteria,
      sort_order: sortOrder,
    })

    if (event.target.checked) {
      searchParams.set('is_new', 'true')
    }
    else {
      searchParams.delete('is_new')
    }
    setSearchParams(searchParams)
  }

  const onCodeSearchSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    const productCodeEntry = new FormData(event.currentTarget).get('product_code')
    const submittedCode = typeof productCodeEntry === 'string' ? productCodeEntry.trim() : ''

    setCodeSearch(previous => ({ ...previous, draft: submittedCode }))

    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)

      if (submittedCode) {
        next.set('product_code', submittedCode)
      }
      else {
        next.delete('product_code')
      }

      return next
    })
  }

  const onCodeSearchClear = () => {
    setCodeSearch(previous => ({ ...previous, draft: '' }))
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      next.delete('product_code')
      return next
    })
  }

  const handleResetFilters = () => {
    const selectedFiltersCount = getSelectedFiltersCount()

    trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCT_FILTERS_RESET, {
      selected_filters_count: selectedFiltersCount,
      new_product_only: searchParams.has('is_new'),
      view_mode: viewMode,
      sort_criteria: sortCriteria,
      sort_order: sortOrder,
    })

    setCodeSearch(previous => ({ ...previous, draft: '' }))

    for (const filterGroup of filterGroups) {
      searchParams.delete(filterGroup.id)
    }

    searchParams.delete('is_new')
    searchParams.delete('product_code')

    setSearchParams(searchParams)
  }

  return (
    <div className="products-gallery__filters-bar" data-testid="filters-bar" aria-busy={isRefreshingFacets}>
      <div className="products-gallery__filters-bar__content">
        <div className="products-gallery__filters-bar__content__header">
          <VpLink variant="tertiary" size="small" onClick={handleResetFilters}>
            {t('products-page.filters.reset')}
          </VpLink>
          <FiltersBarCodeSearch
            label={codeSearchLabel}
            value={productCode}
            onChange={value => setCodeSearch(previous => ({ ...previous, draft: value }))}
            onSubmit={onCodeSearchSubmit}
            onClear={onCodeSearchClear}
          />
          <VpFormControl>
            <VpFormLabel>
              {t('products-page.filters.new-products-only')}
              <VpToggle
                value="my-input-value-3"
                checked={searchParams.has('is_new')}
                name="my-input-play-name-3"
                onChange={onToggleNewProductsOnly}
                size="small"
              />
            </VpFormLabel>
          </VpFormControl>
          <VpDivider />
        </div>
        <VpAccordion
          value={openPanel}
          onChange={(e: string[]) => {
            trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCT_FILTERS_PANEL_TOGGLED, {
              open_panels_count: e.length,
              selected_filters_count: getSelectedFiltersCount(),
              new_product_only: searchParams.has('is_new'),
              view_mode: viewMode,
              sort_criteria: sortCriteria,
              sort_order: sortOrder,
            })
            setOpenPanel(e)
          }}
        >
          {filterGroups.filter(group => !['subdepartments', 'families'].includes(group.id) && group.filters.length > 0).map(group => (
            <FiltersBarGroup key={group.id} group={group} />
          ))}
        </VpAccordion>
      </div>
    </div>
  )
}
